import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Shield, Truck, Diamond, Star } from "lucide-react";
import api from "../../lib/api";
import ProductCard from "../../components/site/ProductCard";

const HERO = "/lifestyle/lifestyle_hero.png";
const STORY = "/lifestyle/lifestyle_story.png";
const LEGACY_IMG = "/products/prod_legacy.png";
const GOLF_IMG = "/lifestyle/lifestyle_golf.png";
const PACKAGE_IMG = "/lifestyle/lifestyle_packaging.png";
const CRAFT_IMG = "/lifestyle/lifestyle_craft.png";
const WOMEN_IMG = "/lifestyle/lifestyle_women.png";

const TESTIMONIALS = [
  { name: "Adewale O., Lagos", body: "First frame in twenty years that didn't slide off the bridge. POCHE understood my face before I did." },
  { name: "Marcus B., New York", body: "Quiet luxury, executed with discipline. People notice — but never the logo. That is the whole point." },
  { name: "Naomi I., London", body: "The Muse is the first feminine silhouette designed for a wider architecture. Finally." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);

  const featured = products.filter((p) => !p.is_legacy).slice(0, 4);
  const bestsellers = products.filter((p) => p.best_seller);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[600px] w-full overflow-hidden bg-poche-black">
        <img src={HERO} alt="Built for real faces" className="absolute inset-0 w-full h-full object-cover opacity-85" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-fade-bottom" />
        <div className="relative container-x h-full flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-2xl">
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold fade-up">Joseph Poche · Atelier 2026</p>
            <h1 className="font-serif text-6xl md:text-8xl mt-5 leading-[0.95] fade-up-d1">
              Built For<br /><span className="text-poche-gold italic">Real</span> Faces.
            </h1>
            <p className="text-white/65 mt-6 max-w-lg text-base md:text-lg leading-relaxed fade-up-d2">
              A house of luxury eyewear designed from the bridge outward — for the wider,
              deeper architecture of the faces the world's frames forgot.
            </p>
            <div className="flex flex-wrap gap-4 mt-10 fade-up-d3">
              <Link to="/shop" data-testid="hero-shop-cta" className="btn-gold">
                Shop the collection <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/legacy" data-testid="hero-legacy-cta" className="btn-outline-gold">
                The Legacy Edition
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-t border-b border-white/5 bg-poche-black">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-6 py-10">
          {[
            { icon: Eye, label: "Designed For Real Faces", sub: "Wider bridge. Better fit." },
            { icon: Diamond, label: "Premium Quality", sub: "Italian acetate. Titanium." },
            { icon: Shield, label: "100% UV Protection", sub: "ZEISS mineral lenses." },
            { icon: Truck, label: "Global Shipping", sub: "Complimentary, insured." },
          ].map((t, i) => (
            <div key={i} className="flex items-start gap-4">
              <t.icon size={28} strokeWidth={1} className="text-poche-gold flex-shrink-0 mt-1" />
              <div>
                <div className="text-[11px] tracking-luxury uppercase text-white">{t.label}</div>
                <div className="text-xs text-white/45 mt-1">{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured collection (ivory background) */}
      <section className="bg-poche-ivory text-poche-black">
        <div className="container-x py-24 md:py-32">
          <div className="flex items-end justify-between mb-14">
            <div>
              <p className="text-[11px] tracking-luxury uppercase text-poche-black/55">Atelier</p>
              <h2 className="font-serif text-4xl md:text-6xl mt-3">Explore The Collection</h2>
            </div>
            <Link to="/shop" data-testid="featured-view-all" className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-luxury uppercase border-b border-poche-black pb-1 hover:text-poche-gold">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {featured.map((p) => <ProductCard key={p.product_id} product={p} light />)}
          </div>
        </div>
      </section>

      {/* Africa to the world story */}
      <section className="container-x py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7 order-2 md:order-1">
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold">From Lagos · To The World</p>
          <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-[1]">
            Designed for the<br />
            <span className="italic text-poche-gold">faces the world forgot.</span>
          </h2>
          <p className="text-white/65 mt-8 max-w-xl leading-relaxed">
            Most luxury eyewear is engineered around a single facial archetype. Joseph Poche was not.
            Our bridge geometry, lens contour, and temple architecture begin where the rest of the industry stops —
            designed for a wider, deeper, more sculpted face. Built for Africa. Built for the diaspora.
            Built, simply, for real faces.
          </p>
          <Link to="/our-story" className="inline-flex items-center gap-3 mt-10 text-[11px] tracking-luxury uppercase border-b border-poche-gold pb-1 text-poche-gold hover:gap-4 transition-all">
            Read our story <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="md:col-span-5 order-1 md:order-2 relative">
          <img src={STORY} alt="Brand story" className="w-full aspect-[4/5] object-cover grayscale" />
          <div className="absolute -bottom-6 -left-6 hidden md:block bg-poche-gold text-poche-black px-6 py-5 max-w-[220px]">
            <div className="num-ticker !text-3xl">147mm</div>
            <div className="text-[10px] tracking-luxury uppercase mt-1">Average frame width — engineered wider.</div>
          </div>
        </div>
      </section>

      {/* Sport / Golf */}
      <section className="grid grid-cols-1 md:grid-cols-2 border-t border-white/5">
        <div className="relative overflow-hidden h-[520px] group">
          <img src={GOLF_IMG} alt="Golf collection" className="product-img absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/50 to-transparent" />
          <div className="absolute bottom-0 left-0 p-10 md:p-14">
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">The Golf Collection</p>
            <h3 className="font-serif text-4xl md:text-5xl text-white mt-3">Engineered<br />for stillness.</h3>
            <Link to="/shop?cat=Golf" data-testid="cta-golf" className="btn-outline-gold mt-7">Shop Golf</Link>
          </div>
        </div>
        <div className="relative overflow-hidden h-[520px] group bg-poche-ink">
          <img src={LEGACY_IMG} alt="Legacy edition" className="product-img absolute inset-0 w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/50 to-transparent" />
          <div className="absolute bottom-0 left-0 p-10 md:p-14">
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Limited · 25 Pieces</p>
            <h3 className="font-serif text-4xl md:text-5xl text-white mt-3">The Legacy<br />Edition.</h3>
            <Link to="/legacy" data-testid="cta-legacy-strip" className="btn-gold mt-7">Request Consultation</Link>
          </div>
        </div>
      </section>

      {/* Best sellers (dark) */}
      <section className="container-x py-24 md:py-32">
        <div className="flex items-end justify-between mb-14">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Most Worn</p>
            <h2 className="font-serif text-4xl md:text-6xl mt-3">Best Sellers</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
          {bestsellers.slice(0, 4).map((p) => <ProductCard key={p.product_id} product={p} light={false} />)}
        </div>
      </section>

      {/* Craftsmanship */}
      <section className="bg-poche-ink border-t border-white/5">
        <div className="container-x py-24 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 relative">
            <img src={CRAFT_IMG} alt="Master craftsman" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-4 -right-4 bg-poche-gold text-poche-black px-5 py-4 max-w-[200px] hidden md:block">
              <div className="num-ticker !text-2xl">19h</div>
              <div className="text-[10px] tracking-luxury uppercase mt-1">Hand-polishing per gold frame.</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Craftsmanship</p>
            <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-tight">Built by hand.<br />Inspected<br /><span className="italic text-poche-gold">by eye.</span></h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-10">
              {[
                { n: "147mm", l: "Average frame width" },
                { n: "100%", l: "ZEISS mineral glass" },
                { n: "2 yr", l: "House warranty" },
                { n: "25", l: "Legacy pieces" },
                { n: "3", l: "Continents per piece" },
                { n: "0", l: "Compromise." },
              ].map((s, i) => (
                <div key={i} className="border-t border-poche-gold/30 pt-5">
                  <div className="num-ticker text-poche-gold !text-3xl">{s.n}</div>
                  <div className="text-xs text-white/60 mt-2 tracking-wider">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Women's Collection editorial */}
      <section className="grid grid-cols-1 md:grid-cols-2 border-t border-white/5">
        <div className="bg-poche-black flex items-center px-8 md:px-20 py-24 order-2 md:order-1">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">The Women's Collection</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">For the women<br /><span className="italic text-poche-gold">who built houses.</span></h2>
            <p className="text-white/60 mt-6 max-w-md leading-relaxed">
              A sculpted silhouette. Hand-set details. A softer bridge contour engineered for a feminine architecture without erasing presence.
            </p>
            <Link to="/shop?cat=Women" data-testid="cta-women" className="btn-outline-gold mt-7 inline-flex">Shop Women</Link>
          </div>
        </div>
        <div className="relative overflow-hidden order-1 md:order-2 min-h-[460px]">
          <img src={WOMEN_IMG} alt="Women's collection" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>

      {/* Packaging / Experience */}
      <section className="grid grid-cols-1 md:grid-cols-2">
        <div className="bg-poche-black flex items-center px-8 md:px-20 py-24">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">The POCHE Experience</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Luxury<br />packaging.<br /><span className="italic text-poche-gold">Timeless presentation.</span></h2>
            <p className="text-white/60 mt-6 max-w-md leading-relaxed">
              Hand-crafted wooden case. Suede pouch. White-glove unboxing. Every detail considered. Every detail held.
            </p>
          </div>
        </div>
        <div className="relative overflow-hidden">
          <img src={PACKAGE_IMG} alt="Packaging" className="w-full h-full object-cover min-h-[460px]" />
        </div>
      </section>

      {/* Testimonials */}
      <section className="container-x py-24 md:py-32">
        <p className="text-[11px] tracking-luxury uppercase text-poche-gold text-center">Worn By</p>
        <h2 className="font-serif text-4xl md:text-5xl text-center mt-4">Those who build legacies.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-16">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="border-t border-poche-gold/30 pt-8">
              <div className="flex gap-1">
                {Array(5).fill(0).map((_, k) => <Star key={k} size={14} fill="#C9A864" stroke="none" />)}
              </div>
              <p className="text-white/75 mt-5 leading-relaxed font-serif text-xl">"{t.body}"</p>
              <p className="text-[11px] tracking-luxury uppercase text-white/40 mt-6">{t.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile App / Admin cta */}
      <section className="bg-poche-ink border-t border-poche-gold/15">
        <div className="container-x py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">The POCHE App</p>
            <h3 className="font-serif text-4xl md:text-5xl mt-3">Virtual try-on. VIP drops. Concierge in your pocket.</h3>
          </div>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <Link to="/app" data-testid="open-mobile-app" className="btn-gold">Open Mobile App</Link>
            <Link to="/admin/login" data-testid="open-admin" className="btn-outline-gold">Admin Portal</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
